import React from 'react'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import EmptyListUI from '../../../widgets/output/EmptyListUI'
import SpinnerUI from '../../../widgets/output/SpinnerUI'
import { Link } from 'react-router-dom'
import './DailyPaymentsReportUI.css'

const parseIndianAmount = (value) => {
  if (value === null || value === undefined || value === '' || value === '-') return null
  const cleaned = value.toString()
    .replace(/[₹,\s]/g, '')
    .replace(/^\((.*)\)$/, '-$1')
  const num = parseFloat(cleaned)
  return Number.isFinite(num) ? num : null
}

const PROPERTIES_LINK_BY_CODE = {
  B: 'fd_balance_in_733',
  C: 'csfb_fd',
  F: 'fd_interest_in_733',
}
const PROPERTIES_LINK_TITLE = 'Update amount'

const AmountCell = ({ value, variant, rowCode }) => {
  const display = value || '-'
  const propertyCode = PROPERTIES_LINK_BY_CODE[rowCode]
  const className = [
    'dpr_amount',
    variant === 'danger' ? 'dpr_amount_danger' : '',
    variant === 'pending' ? 'dpr_amount_pending' : '',
  ].filter(Boolean).join(' ')

  const content = variant === 'summary' ? <strong>{display}</strong> : display

  return (
    <td className={className}>
      {propertyCode ? (
        <Link to={`/su/properties?highlight=${propertyCode}`} className='dpr_amount_link' title={PROPERTIES_LINK_TITLE}>
          {content}
        </Link>
      ) : content}
    </td>
  )
}

const isDiscrepancyAmount = (value) => {
  const num = parseIndianAmount(value)
  return num !== null && num !== 0
}

const RowCode = ({ code }) => {
  if (!code) return null
  const isSubCode = String(code).includes('.')
  return (
    <span className={`dpr_row_code ${isSubCode ? 'dpr_row_code_sub' : ''}`} title={`Reference ${code}`}>
      {code}
    </span>
  )
}

const ReportAmountRow = ({ rowCode, label, value, variant, indent }) => (
  <tr className={`dpr_row ${variant ? `dpr_row_${variant}` : ''}`}>
    <td className={`dpr_label ${indent ? 'dpr_label_indent' : ''}`}>
      <span className='dpr_label_content'>
        <RowCode code={rowCode} />
        <span className='dpr_label_text'>{label}</span>
      </span>
    </td>
    <AmountCell
      rowCode={rowCode}
      value={value}
      variant={variant === 'danger' ? 'danger' : (variant === 'summary' ? 'summary' : '')}
    />
  </tr>
)

const SectionHeaderRow = ({ title }) => (
  <tr className='dpr_section_header'>
    <th colSpan={2}>{title}</th>
  </tr>
)

const KpiCard = ({ title, value, alert }) => (
  <article className={`dpr_kpi_card ${alert ? 'dpr_kpi_card_alert' : ''}`}>
    <h3 className='dpr_kpi_title'>{title}</h3>
    <p className={`dpr_kpi_value ${alert ? 'dpr_kpi_value_alert' : ''}`}>{value || '-'}</p>
  </article>
)

const DailyPaymentsReportUI = ({
  context, report, reportDateISO, isCurrentDayReport, onRefreshClick,
  onPreviousClick, onNextClick, formatReportDate, onDownloadClick, downloadMode, onSendEmailClick, emailSending
}) => {
  const hasSavedReport = report && report.dateISO
  const pendingTransactions = hasSavedReport && Array.isArray(report.pendingTransactions) ? report.pendingTransactions : []

  const differenceValue = report && report.differenceWithBankF4
  const showDifferenceAlert = isDiscrepancyAmount(differenceValue)

  return (
    <div className='daily_payments_report'>
      <LRTableActionsUI location='top'
        left={
          <span className='g_flex_left g_flex_h'>
            <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} />
            <span className='daily_payments_icon_group'>
              <IconButtonUI icon='icon-download' onClick={onDownloadClick} label='Excel' />
              <SpinnerUI className='daily_payments_download_spinner' reserveSpace={false} show={downloadMode} />
              {isCurrentDayReport &&
                <>
                  <IconButtonUI icon='icon-envelope-o' onClick={onSendEmailClick} label='Send Email' />
                  <SpinnerUI className='daily_payments_download_spinner' reserveSpace={false} show={emailSending} />
                </>
              }
            </span>
            <span className='g_m_left_100 g_large'>
              <b>Daily Payments report</b>
              {reportDateISO && <span className='g_m_left_50 g_muted'>{formatReportDate(reportDateISO)}</span>}
            </span>
          </span>
        }
        right={
          <span>
            <Link to='#' onClick={(e) => { e.preventDefault(); onPreviousClick() }}>Previous</Link>
            {!isCurrentDayReport &&
              <>
                <span className='g_m_left_100' />
                <Link to='#' onClick={(e) => { e.preventDefault(); onNextClick() }}>Next</Link>
              </>
            }
          </span>
        }
      />

      {!hasSavedReport &&
        <EmptyListUI
          context={context}
          createEnabled={false}
          createDisabledMessageLabel='No saved record'
        />
      }

      {hasSavedReport &&
        <section className='dpr_content g_m_top_50'>
          <div className='dpr_kpi_grid'>
            <KpiCard title='Total as per Bank' value={report.totalAsPerBankF4} />
            <KpiCard title='Total as per Console' value={report.totalF4} />
            <KpiCard
              title='Difference with Bank'
              value={differenceValue}
              alert={showDifferenceAlert}
            />
          </div>

          <table className='dpr_table'>
            <thead>
              <tr>
                <th className='dpr_col_label'>Particulars</th>
                <th className='dpr_col_amount'>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <SectionHeaderRow title='As per Bank' />
              <ReportAmountRow rowCode='A' label='Current balance in 733' value={report.latest733BalanceF4} />
              <ReportAmountRow rowCode='B' label='Current FD Balance in 733' value={report.currentFDBalanceIn733F4} />
              <ReportAmountRow rowCode='C' label='Current FD Balance in CSFB' value={report.currentFDBalanceInCSFBF4} />
              <ReportAmountRow rowCode='D' label='Total as per Bank (A + B + C)' value={report.totalAsPerBankF4} variant='summary' />

              <SectionHeaderRow title='As per Console' />
              <ReportAmountRow
                rowCode='E'
                label='Current balance across all Escrow Accounts'
                value={report.currentBalanceAcrossAllEscrowsF4}
                variant='summary'
              />
              <ReportAmountRow rowCode='E.1' label='Current DSRA of all active accounts' value={report.totalDSRAF4} indent />
              <ReportAmountRow rowCode='E.2' label='Surplus (Escrow − DSRA)' value={report.totalSurplusF4} indent />
              <ReportAmountRow rowCode='F' label='Current FD Interest' value={report.currentFDInterestF4} />
              <ReportAmountRow rowCode='G' label='Total as per Console (E + F)' value={report.totalF4} variant='summary' />
              <ReportAmountRow rowCode='H' label='Difference with Bank (A − G)' value={report.differenceWithBankF4} variant='danger' />

              <SectionHeaderRow title='Pending approvals' />
              <ReportAmountRow
                rowCode='I'
                label='Transactions pending for approval'
                value={report.sumOfPendingTransactionsF4}
                variant='summary'
              />
            </tbody>
          </table>

          {pendingTransactions.length > 0 &&
            <table className='dpr_table dpr_txn_table'>
              <thead>
                <tr>
                  <th className='dpr_col_label'>Transaction Description</th>
                  <th className='dpr_col_amount'>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {pendingTransactions.map((txn, idx) => (
                  <tr key={txn.transactionId || idx} className='dpr_row dpr_row_txn'>
                    <td className='dpr_txn_description'>
                      <span className='dpr_label_content'>
                        <RowCode code={String(idx + 1)} />
                        <span className='dpr_label_text'>{txn.narration || 'Transaction'}</span>
                      </span>
                    </td>
                    <AmountCell value={txn.amountF5} variant='pending' />
                  </tr>
                ))}
              </tbody>
            </table>
          }

          <table className='dpr_table dpr_table_footer'>
            <tbody>
              <SectionHeaderRow title='Proposed closing balances' />
              <ReportAmountRow
                rowCode='J'
                label='Proposed Closing Balance in 733 (A − I)'
                value={report.proposedClosingBalanceIn733F4}
                variant='summary'
              />
              <ReportAmountRow
                rowCode='H'
                label='Proposed Closing Balance in bank (J + B + C)'
                value={report.proposedClosingBalanceInBankF4}
                variant='summary'
              />
            </tbody>
          </table>
        </section>
      }
    </div>
  )
}

export default DailyPaymentsReportUI
